import { userEntryToNumber } from '@ebiz/pretty-num/';

const hasAnnotation = function (value: string): boolean {
  if (value.trim().split(' ').length > 1) {
    return true;
  }
  return false;
};

const engineeringComparatorCore = function (value1: string, value2: string, isLow = true): 1 | 0 | -1 {
  /**
   * currencyRegExp
   * matches the character with currency symbol for (us,ru,cn,jp) only
   * followed by digits (equivalent to [0-9]
   *
   * areaRegExp
   * matches float numbers
   * followed by the characters mm literally (case sensitive)
   */

  // eslint-disable-next-line no-useless-escape
  const currencyRegExp = /[\$¥₽]\d/;
  const areaRegExp = /[-+]?[0-9]*\.?[0-9]+mm/;
  // checking value 1
  if (typeof value1 === 'string' && value1.length > 1) {
    if (hasAnnotation(value1)) {
      const fieldValue1 = value1.trim().split(' ')[0] ?? '';
      if (currencyRegExp.test(fieldValue1)) {
        value1 = fieldValue1.slice(1);
      }
      if (areaRegExp.test(fieldValue1)) {
        value1 = fieldValue1.replace(/[^\d.-]/g, '');
      }
    }
  }
  // checking value 2
  if (typeof value2 === 'string' && value2.length > 1) {
    if (hasAnnotation(value2)) {
      const fieldValue2 = value2.trim().split(' ')[0] ?? '';
      if (currencyRegExp.test(fieldValue2)) {
        value2 = fieldValue2.slice(1);
      }
      if (areaRegExp.test(fieldValue2)) {
        value2 = fieldValue2.replace(/[^\d.-]/g, '');
      }
    }
  }

  const comparisonValueForNonNumeric = isLow ? Infinity : -Infinity;
  const isValue1Blank: boolean = value1 === '' || value1 === '-';
  const isValue2Blank: boolean = value2 === '' || value2 === '-';
  value1 = isValue1Blank ? '0' : value1;
  value2 = isValue2Blank ? '0' : value2;
  let _obj1 = userEntryToNumber(value1 ? value1.toString() : value1, comparisonValueForNonNumeric);
  let _obj2 = userEntryToNumber(value2 ? value2.toString() : value2, comparisonValueForNonNumeric);
  if (isNaN(_obj1)) {
    _obj1 = comparisonValueForNonNumeric;
  }
  if (isNaN(_obj2)) {
    _obj2 = comparisonValueForNonNumeric;
  }
  return _obj1 < _obj2
    ? -1
    : _obj1 > _obj2
      ? 1
      : isValue1Blank && !isValue2Blank
        ? -1
        : !isValue1Blank && isValue2Blank
          ? 1
          : 0;
};

export default {
  partComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    const _obj1 = /([A-Za-z]+)([0-9]+)(.*)/.exec(value1);
    const _obj2 = /([A-Za-z]+)([0-9]+)(.*)/.exec(value2);
    let result: 1 | 0 | -1 = 0;
    if (_obj1 != null && _obj2 != null && _obj1[1] != null && _obj2[1] != null) {
      result = _obj1[1] < _obj2[1] ? -1 : _obj1[1] > _obj2[1] ? 1 : 0;
      if (result === 0 && _obj1[2] != null && _obj2[2] != null) {
        const _num1 = parseInt(_obj1[2]);
        const _num2 = parseInt(_obj2[2]);
        result = _num1 < _num2 ? -1 : _num1 > _num2 ? 1 : 0;
      }
      if (result === 0 && _obj1[3] != null && _obj2[3] != null) {
        result = _obj1[3] < _obj2[3] ? -1 : _obj1[3] > _obj2[3] ? 1 : 0;
      }
    }
    return result;
  },

  engineeringComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    return engineeringComparatorCore(value1, value2);
  },

  engineeringComparatorNonNumbersHigh: function (value1: string, value2: string): 1 | 0 | -1 {
    return engineeringComparatorCore(value1, value2, false);
  },

  engineeringComparatorNonNumbersLow: function (value1: string, value2: string): 1 | 0 | -1 {
    return engineeringComparatorCore(value1, value2, true);
  },

  priceComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    const isValue1Blank: boolean = value1 === '' || value1 === '-';
    const isValue2Blank: boolean = value2 === '' || value2 === '-';
    let _obj1 = isValue1Blank ? 0 : -Infinity;
    let _obj2 = isValue2Blank ? 0 : -Infinity;
    if (typeof value1 === 'string' && value1.length > 1) {
      _obj1 = parseFloat(value1.slice(1));
    }
    if (typeof value2 === 'string' && value2.length > 1) {
      _obj2 = parseFloat(value2.slice(1));
    }
    if (isNaN(_obj1)) {
      _obj1 = -Infinity;
    }
    if (isNaN(_obj2)) {
      _obj2 = -Infinity;
    }
    return _obj1 < _obj2
      ? -1
      : _obj1 > _obj2
        ? 1
        : isValue1Blank && !isValue2Blank
          ? -1
          : !isValue1Blank && isValue2Blank
            ? 1
            : 0;
  },

  rangeComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    let _obj1 = -Infinity;
    let _obj2 = -Infinity;
    if (typeof value1 === 'string' && value1.length > 3 && /.+ ~ /.test(value1)) {
      _obj1 = userEntryToNumber(value1.split(' ~ ')[0] ?? ''); //We know the ?? clause won't happen due to the guard RE test
    }
    if (typeof value2 === 'string' && value2.length > 3 && /.+ ~ /.test(value2)) {
      _obj2 = userEntryToNumber(value2.split(' ~ ')[0] ?? ''); //We know the ?? clause won't happen due to the guard RE test
    }
    if (isNaN(_obj1)) {
      _obj1 = -Infinity;
    }
    if (isNaN(_obj2)) {
      _obj2 = -Infinity;
    }
    return _obj1 < _obj2 ? -1 : _obj1 > _obj2 ? 1 : 0;
  },

  integerComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    const isValue1Blank: boolean = value1 === '' || value1 === '-';
    const isValue2Blank: boolean = value2 === '' || value2 === '-';
    let _obj1 = isValue1Blank ? 0 : parseInt(value1);
    let _obj2 = isValue2Blank ? 0 : parseInt(value2);
    if (isNaN(_obj1)) {
      _obj1 = -Infinity;
    }
    if (isNaN(_obj2)) {
      _obj2 = -Infinity;
    }
    return _obj1 < _obj2
      ? -1
      : _obj1 > _obj2
        ? 1
        : isValue1Blank && !isValue2Blank
          ? -1
          : !isValue1Blank && isValue2Blank
            ? 1
            : 0;
  },

  textComparator: function (value1: string, value2: string): 1 | 0 | -1 {
    return value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
  },

  booleanComparator: function (value1: boolean, value2: boolean): 1 | 0 | -1 {
    return value1 === value2 ? 0 : value1 ? -1 : 1;
  },
};
